import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Html5Qrcode } from 'html5-qrcode';
import { ScanLine } from 'lucide-react';
import BaseModal from './core/BaseModal';

const BarcodeScanner = ({ isOpen, onClose, onScan }) => {
  const html5QrCodeRef = useRef(null);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current
          .stop()
          .catch((err) => console.error('Error stopping scanner:', err))
          .finally(() => {
            html5QrCodeRef.current = null;
            hasStartedRef.current = false;
          });
      }
      return;
    }

    const startScanner = async () => {
      if (hasStartedRef.current) return;

      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          alert(
            'دوربین در این مرورگر پشتیبانی نمی‌شود.\n\n' +
              'لطفاً اطمینان حاصل کنید که:\n' +
              '1. از HTTPS استفاده می‌کنید (نه HTTP)\n' +
              '2. مرورگر شما به‌روز است\n' +
              '3. دسترسی دوربین در تنظیمات مرورگر فعال است',
          );
          onClose();
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 100));

        const html5QrCode = new Html5Qrcode('barcode-scanner-reader');
        html5QrCodeRef.current = html5QrCode;
        hasStartedRef.current = true;

        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
            disableFlip: false,
          },
          (decodedText) => {
            if (html5QrCodeRef.current) {
              html5QrCodeRef.current
                .stop()
                .then(() => {
                  html5QrCodeRef.current = null;
                  hasStartedRef.current = false;
                  onScan(decodedText);
                  onClose();
                })
                .catch((err) => console.error('Error stopping scanner:', err));
            }
          },
          () => {
            // Ignore scanning errors (happens continuously while scanning)
          },
        );
      } catch (err) {
        console.error('Error starting scanner:', err);
        hasStartedRef.current = false;

        let errorMsg = 'خطا در باز کردن دوربین.\n\n';

        if (
          err.name === 'NotAllowedError' ||
          err.name === 'PermissionDeniedError'
        ) {
          errorMsg += 'دسترسی به دوربین رد شد.\n\n';
          errorMsg += 'در iOS Safari:\n';
          errorMsg += 'Settings > Safari > Camera > Allow\n\n';
          errorMsg += 'سپس صفحه را رفرش کنید.';
        } else if (
          err.name === 'NotFoundError' ||
          err.name === 'DevicesNotFoundError'
        ) {
          errorMsg += 'دوربینی یافت نشد.';
        } else if (
          err.name === 'NotReadableError' ||
          err.name === 'TrackStartError'
        ) {
          errorMsg += 'دوربین در حال استفاده توسط برنامه دیگری است.';
        } else if (err.name === 'OverconstrainedError') {
          errorMsg += 'تنظیمات دوربین پشتیبانی نمی‌شود.';
        } else if (err.name === 'NotSupportedError') {
          errorMsg += 'این مرورگر از دوربین پشتیبانی نمی‌کند.\n';
          errorMsg += 'لطفاً از HTTPS استفاده کنید.';
        } else {
          errorMsg += 'نوع خطا: ' + (err.name || 'نامشخص') + '\n';
          errorMsg += 'پیام: ' + (err.message || err || 'بدون پیام');
        }

        alert(errorMsg);
        onClose();
      }
    };

    startScanner();
  }, [isOpen, onClose, onScan]);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="اسکن بارکد"
      icon={<ScanLine size={24} />}
      iconVariant="info"
      size="md"
      closeOnBackdropClick={false}
    >
      <div className="p-6">
        <div
          id="barcode-scanner-reader"
          className="w-full overflow-hidden rounded-lg bg-black"
        ></div>

        <p className="text-tertiary-70 mt-4 text-center text-sm dark:text-gray-400">
          بارکد یا QR Code را در مقابل دوربین قرار دهید
        </p>

        <div className="mt-6 flex justify-center">
          <button
            onClick={onClose}
            className="bg-tertiary-20 text-tertiary-90 hover:bg-tertiary-30 rounded-xl px-6 py-2.5 font-medium shadow-sm transition-all duration-200 hover:shadow-md dark:bg-gray-700 dark:text-gray-200 dark:shadow-md dark:hover:bg-gray-600"
          >
            انصراف
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

BarcodeScanner.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onScan: PropTypes.func.isRequired,
};

export default BarcodeScanner;
